/**
 * AnnuaireAgentsPage — l'annuaire INTERNE des agents (item 6.3, mig 286).
 *
 * ─── CE QU'IL N'EST PAS ─────────────────────────────────────────────────────
 *
 * Ce n'est pas l'annuaire des tiers (`/admin/annuaire`, mig 213/224), qui liste
 * les fournisseurs et les prestataires. Ici, ce sont les collègues. Deux écrans,
 * deux permissions, deux objets — et un nom de route distinct pour qu'on ne les
 * confonde pas dans six mois.
 *
 * ─── POURQUOI IL NE SE CLOISONNE PAS ────────────────────────────────────────
 *
 * L'item demande un « accès mutualisé ». Un agent de la GRC doit pouvoir
 * appeler un agent du service technique sans passer par sa hiérarchie. C'est la
 * deuxième brique, après « Mon espace agent », à s'ouvrir de la même façon à
 * tous les services.
 *
 * ─── CE QU'ON NE VERRA JAMAIS ICI ───────────────────────────────────────────
 *
 * Ni coordonnée personnelle (le back ne les sélectionne même pas), ni MOTIF
 * d'absence. Un collègue absent est « absent aujourd'hui » — pas « en arrêt
 * maladie », qui serait une donnée de santé diffusée à toute la commune.
 * L'écran ne peut pas l'afficher : l'API ne le lui envoie pas.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, Alert, Badge, Button, Input, ModuleHeader, Pill } from '@/components/ui';
import { EmptyState } from '@/components/ui/EmptyState';
import { PhoneLink } from '@/components/ui/PhoneLink';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type {
  AgentAnnuaire, AnnuaireInterneReglages,
} from '@roronoa-collectivites/shared-types';
import {
  Users, Search, Mail, Clock, Settings2, Loader2, Info, CircleCheck, CircleMinus,
} from 'lucide-react';

/** Mêmes clés que `UserService`, libellés métier — alignés sur DiffusionNouvellePage. */
const LIBELLE_SERVICE: Record<string, string> = {
  grc: 'Relation citoyen',
  service_technique: 'Service technique',
  scolaire_cantine: 'Scolaire & cantine',
  petite_enfance: 'Petite enfance',
  associations: 'Associations',
  urbanisme: 'Urbanisme',
  comptable: 'Comptabilité',
  rh: 'Ressources humaines',
};

/** Le maire et les adjoints n'ont pas de secteur : `users.service` y est NULL. */
function libelleService(a: AgentAnnuaire): string {
  if (a.service) return LIBELLE_SERVICE[a.service] ?? a.service;
  return a.role === 'admin' ? 'Élus & direction' : 'Sans secteur';
}

export function AnnuaireAgentsPage() {
  const { user } = useAuth();
  const estAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [agents, setAgents] = useState<AgentAnnuaire[]>([]);
  const [reglages, setReglages] = useState<AnnuaireInterneReglages | null>(null);
  const [jour, setJour] = useState('');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [recherche, setRecherche] = useState('');
  const [presentsSeuls, setPresentsSeuls] = useState(false);
  const [panneauReglages, setPanneauReglages] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const r = await api.annuaireInterne.lister();
        if (!vivant) return;
        setAgents(r.agents);
        setReglages(r.reglages);
        setJour(r.jour);
      } catch (e) {
        if (vivant) setErreur(e instanceof ApiException ? e.message : 'Annuaire indisponible');
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    return () => { vivant = false; };
  }, []);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return agents.filter((a) => {
      if (presentsSeuls && a.present === false) return false;
      if (!q) return true;
      return [a.prenom, a.nom, a.fonction ?? '', libelleService(a)]
        .join(' ').toLowerCase().includes(q);
    });
  }, [agents, recherche, presentsSeuls]);

  /** Regroupement par secteur : on cherche presque toujours « quelqu'un de la voirie ». */
  const parService = useMemo(() => {
    const m = new Map<string, AgentAnnuaire[]>();
    for (const a of filtres) {
      const k = libelleService(a);
      const liste = m.get(k);
      if (liste) liste.push(a); else m.set(k, [a]);
    }
    return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0], 'fr'));
  }, [filtres]);

  const nbPresents = agents.filter((a) => a.present === true).length;

  async function enregistrerReglages(patch: Partial<AnnuaireInterneReglages>) {
    if (!reglages) return;
    setEnregistrement(true);
    try {
      const out = await api.annuaireInterne.majReglages({ ...reglages, ...patch });
      setReglages(out);
      // Les colonnes masquées doivent disparaître tout de suite : on relit
      // plutôt que de deviner localement ce que le back aurait renvoyé.
      const r = await api.annuaireInterne.lister();
      setAgents(r.agents);
    } catch (e) {
      setErreur(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally {
      setEnregistrement(false);
    }
  }

  return (
    <>
      <AppHeader />
      <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-4">
        <ModuleHeader
          accent="rh"
          icon={Users}
          eyebrow="Espace interne"
          title="Annuaire des agents"
        />

        {erreur && <Alert variant="error" className="mt-4">{erreur}</Alert>}

        {chargement ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-ink-subtle">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement de l'annuaire…
          </div>
        ) : (
          <>
            <Card className="mt-4">
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
                    aria-hidden="true"
                  />
                  <Input
                    className="pl-9"
                    placeholder="Un nom, un poste, un service…"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    aria-label="Rechercher un agent"
                  />
                </div>

                {reglages?.presenceVisible && (
                  <button
                    type="button"
                    onClick={() => setPresentsSeuls((v) => !v)}
                    aria-pressed={presentsSeuls}
                    className={`inline-flex h-9 items-center gap-2 rounded-r-lg border px-3 text-sm ${
                      presentsSeuls
                        ? 'border-rh/50 bg-rh/10 font-medium'
                        : 'border-border bg-surface/50'
                    }`}
                  >
                    <CircleCheck className="h-4 w-4" aria-hidden="true" />
                    Présents aujourd'hui
                    <Badge variant="success" size="sm">{nbPresents}</Badge>
                  </button>
                )}

                {estAdmin && (
                  <Button
                    variant="ghost"
                    onClick={() => setPanneauReglages((v) => !v)}
                    aria-expanded={panneauReglages}
                  >
                    <Settings2 className="h-4 w-4" aria-hidden="true" /> Réglages
                  </Button>
                )}
              </div>

              {estAdmin && panneauReglages && reglages && (
                <div className="border-t border-border px-4 py-3">
                  <p className="mb-3 flex items-start gap-2 text-xs text-ink-subtle">
                    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    Ce que l'annuaire laisse voir à l'ensemble des agents. Le motif
                    d'une absence n'est jamais affiché, quel que soit ce réglage.
                  </p>
                  <div className="flex flex-col gap-2">
                    {([
                      ['telephoneVisible', 'Afficher les téléphones de travail'],
                      ['planningVisible', 'Afficher les horaires du jour'],
                      ['presenceVisible', 'Afficher qui est présent'],
                    ] as const).map(([cle, label]) => (
                      <label key={cle} className="flex items-center gap-2 text-sm text-ink">
                        <input
                          type="checkbox"
                          checked={reglages[cle]}
                          disabled={enregistrement}
                          onChange={(e) => void enregistrerReglages({ [cle]: e.target.checked })}
                          className="h-4 w-4 rounded-r-sm border-border"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {filtres.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Aucun agent ne correspond"
                description="Essayez un autre nom, ou retirez le filtre de présence."
              />
            ) : (
              parService.map(([service, liste]) => (
                <section key={service} className="mt-6">
                  <h2 className="mb-2 text-sm font-medium text-ink-subtle">
                    {service} <span className="font-normal">({liste.length})</span>
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {liste.map((a) => (
                      <Card key={a.id}>
                        <div className="flex items-start justify-between gap-3 p-4">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-ink">
                              {a.prenom} {a.nom}
                            </p>
                            {a.fonction && (
                              <p className="truncate text-sm text-ink-subtle">{a.fonction}</p>
                            )}

                            <div className="mt-2 flex flex-col gap-1 text-sm">
                              {a.email && (
                                <a
                                  href={`mailto:${a.email}`}
                                  className="inline-flex items-center gap-2 text-ink-subtle hover:text-ink"
                                >
                                  <Mail className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                  <span className="truncate">{a.email}</span>
                                </a>
                              )}
                              {/* PhoneLink, jamais <a href="tel:"> : sur desktop il
                                  ouvrirait la modale système de Chrome (règle R13). */}
                              {a.telephone && <PhoneLink number={a.telephone} />}
                              {a.horairesJour && (
                                <span className="inline-flex items-center gap-2 text-ink-subtle">
                                  <Clock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                  {a.horairesJour}
                                </span>
                              )}
                            </div>
                          </div>

                          {a.present !== null && (
                            a.present
                              ? <Pill tone="success" size="md">Présent</Pill>
                              : <Pill tone="neutral" size="md">
                                  <CircleMinus className="h-3.5 w-3.5" aria-hidden="true" /> Absent
                                </Pill>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </section>
              ))
            )}

            {reglages?.presenceVisible && jour && (
              <p className="mt-6 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                Présence constatée pour le {jour}. Un agent est présent sauf congé
                ou absence validée — l'annuaire n'indique jamais le motif.
              </p>
            )}
          </>
        )}
      </main>
    </>
  );
}
